import { useState, useCallback, useMemo, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as yup from 'yup';
import { Formik, Form, useFormikContext } from 'formik';
import classNames from 'classnames';

import { siteQuery } from '/src/features/_global/queries';
import { useMutation, useNotifications, useDebounce, usePrevious, usePermissions } from '/src/hooks';
import { setRecordingSettings, setSiteRecording } from '/src/features/options/mutations';
import {
  noDuplicationInValidation,
  getInitialValues,
  returnArrayOfURLsForSubmission,
  pageTargetingEnabledType,
} from '/src/features/options/utils';
import { superLenientUrlRegex } from '/src/utils/regex';
import { isValidCssSelector } from '@crazyegginc/js-utils';
import { normalizeUrl, isOnSiteHost } from '/src/utils/url';
import { changeInteractiveMode } from '/src/services/web-editor';

import { Panel, Button, Toggle, SkeletonLine, Input, RadioGroup, Tooltip } from '@crazyegginc/hatch';
import { OptionsHeader } from '/src/features/options/components/OptionsHeader';
import { SiteSettingsModalListofInputs } from '/src/features/options/components/modals/SiteSettingsModalListofInputs';

import { PAGE_TARGETING_ENABLED_TYPE } from '/src/features/options/constants';
import { ElementSelector } from '/src/components/ElementSelector';
import { SupportLinks } from '/src/support';
import { SupportLink } from '/src/features/_global/components/SupportLink';

import { ReactComponent as SearchIcon } from '@crazyegginc/hatch/dist/images/icon-search-filled.svg';
import { ReactComponent as DesktopIcon } from '@crazyegginc/hatch/dist/images/icon-desktop-outline.svg';
import { ReactComponent as TabletIcon } from '@crazyegginc/hatch/dist/images/icon-tablet-outline.svg';
import { ReactComponent as MobileIcon } from '@crazyegginc/hatch/dist/images/icon-mobile-outline.svg';
import { ReactComponent as InteractiveIcon } from '@crazyegginc/hatch/dist/images/icon-interactive-outline.svg';
import { ReactComponent as ReloadIcon } from '@crazyegginc/hatch/dist/images/icon-redo.svg';
import { DEVICE_TYPES, FEATURES } from '/src/features/_global/constants';

const { DESKTOP, TABLET, PHONE } = DEVICE_TYPES;
const { RECORD_ALL, RECORD_BY_JS, RECORD_SPECIFIC } = PAGE_TARGETING_ENABLED_TYPE;

function AutoSave({ enabled }) {
  const { values, isValid, dirty, isSubmitting, submitForm } = useFormikContext();

  const debouncedValues = useDebounce(values, 1250);
  const prevDebouncedValues = usePrevious(debouncedValues);

  const hasOnlyEmptyAdditions = useMemo(() => {
    if (!prevDebouncedValues) return false;

    const clean = (arr, key) => arr?.filter((r) => r[key]?.trim() !== '') ?? [];

    const prevCleaned = {
      urlMatchingRules: clean(prevDebouncedValues.urlMatchingRules, 'u'),
      blockedUrlMatchingRules: clean(prevDebouncedValues.blockedUrlMatchingRules, 'u'),
      maskElements: clean(prevDebouncedValues.maskElements, 'selector'),
      pageTargetingEnabledType: prevDebouncedValues.pageTargetingEnabledType,
      endSessionOnBlockedUrl: prevDebouncedValues.endSessionOnBlockedUrl,
    };

    const currCleaned = {
      urlMatchingRules: clean(debouncedValues?.urlMatchingRules, 'u'),
      blockedUrlMatchingRules: clean(debouncedValues?.blockedUrlMatchingRules, 'u'),
      maskElements: clean(debouncedValues?.maskElements, 'selector'),
      pageTargetingEnabledType: debouncedValues?.pageTargetingEnabledType,
      endSessionOnBlockedUrl: debouncedValues?.endSessionOnBlockedUrl,
    };

    return JSON.stringify(prevCleaned) === JSON.stringify(currCleaned);
  }, [debouncedValues, prevDebouncedValues]);

  useEffect(() => {
    if (
      enabled &&
      isValid &&
      dirty &&
      !isSubmitting &&
      !hasOnlyEmptyAdditions &&
      prevDebouncedValues &&
      prevDebouncedValues !== debouncedValues
    ) {
      submitForm();
    }
  }, [debouncedValues, enabled, prevDebouncedValues, isValid, dirty, isSubmitting, hasOnlyEmptyAdditions, submitForm]);

  return null;
}

// eslint-disable-next-line no-restricted-syntax
export default function RecordingsSettings() {
  const { site, siteId, siteName, loadingSite } = useOutletContext();
  const notifications = useNotifications();

  const permissions = usePermissions();
  const canManageRecordingsSettings = permissions.can('settings', FEATURES.RECORDINGS).allowed;

  const { mutate: mutateUpdateSiteRecording } = useMutation(setSiteRecording);
  const { mutateAsync: mutateRecordingSettingsAsync } = useMutation(setRecordingSettings);

  const { data } = useQuery({
    ...siteQuery({
      id: siteId,
    }),
  });

  const resolvedSite = data?.site ?? site;
  const isRecordingSessionEnabled = resolvedSite?.sessionRecording ?? false;
  const canEditRecordingSettings = canManageRecordingsSettings && isRecordingSessionEnabled;

  if (loadingSite || !siteName) {
    return (
      <div className="space-y-10">
        <Panel>
          <SkeletonLine />
        </Panel>
      </div>
    );
  }

  const validationSchema = yup.object().shape({
    pageTargetingEnabledType: yup.string().oneOf([RECORD_ALL, RECORD_BY_JS, RECORD_SPECIFIC]),
    urlMatchingRules: yup.array().when('pageTargetingEnabledType', {
      is: (value) => value === RECORD_SPECIFIC,
      then: yup
        .array()
        .of(
          yup.object().shape({
            u: yup
              .string()
              .required('Please provide a URL.')
              .test('is-valid-url', 'This URL is not valid. Please correct it and try again.', (value) => {
                if (!value) return false;
                const cleanValue = value.replace(/^\*:\/\//, '');
                return cleanValue.match(superLenientUrlRegex);
              })
              .test('is-on-site', `The provided URL must be on ${siteName}.`, (value) => {
                if (!value) return false;
                return isOnSiteHost(value, siteName);
              }),
          }),
        )
        .test({
          name: 'is-duplicate',
          test: function (value) {
            const isDuplicate = noDuplicationInValidation(value, 'u');
            return !isDuplicate
              ? true
              : this.createError({
                  message: 'Please provide a unique URL.',
                });
          },
        }),
    }),
    blockedUrlMatchingRules: yup
      .array()
      .of(
        yup.object().shape({
          u: yup
            .string()
            .test('is-valid-url', 'This URL is not valid. Please correct it and try again.', (value) => {
              if (!value) return true; // allow empty
              const cleanValue = value.replace(/^\*:\/\//, '');
              return cleanValue.match(superLenientUrlRegex);
            })
            .test('is-on-site', `The provided URL must be on ${siteName}.`, (value) => {
              if (!value) return true; // allow empty
              return isOnSiteHost(value, siteName);
            }),
        }),
      )
      .test({
        name: 'is-url-duplicate',
        test: function (value) {
          const isDuplicate = noDuplicationInValidation(value, 'u');
          return !isDuplicate
            ? true
            : this.createError({
                message: 'Please provide a unique URL.',
              });
        },
      }),
    maskElements: yup
      .array()
      .of(
        yup.object().shape({
          selector: yup
            .string()
            .max(10000, 'Your CSS selector exceeds the maximum length of 10,000 characters.')
            .test('css-selector', 'Please enter a valid CSS selector.', (value) => isValidCssSelector(value)),
        }),
      )
      .test({
        name: 'is-css-duplicate',
        test: function (value) {
          const isDuplicate = noDuplicationInValidation(value, 'selector');

          return !isDuplicate
            ? true
            : this.createError({
                message: 'Please provide a unique CSS selector.',
              });
        },
      }),
  });

  const initialValues = {
    pageTargetingEnabledType: pageTargetingEnabledType(
      resolvedSite?.recordingSettings?.pageTargetingEnabled,
      resolvedSite?.recordingSettings?.urlMatchingRules,
    ),
    endSessionOnBlockedUrl: getInitialValues('bool', resolvedSite?.recordingSettings?.endSessionOnBlockedUrl),
    urlMatchingRules: getInitialValues('url', resolvedSite?.recordingSettings?.urlMatchingRules),
    blockedUrlMatchingRules: getInitialValues('url', resolvedSite?.recordingSettings?.blockedUrlMatchingRules),
    maskElements: getInitialValues('css', resolvedSite?.recordingSettings?.maskElements),
  };

  const recordingSessionStatus = !isRecordingSessionEnabled ? 'on' : 'off';

  return (
    <>
      <div className="mb-10">
        <Panel>
          <Toggle
            labelClasses="!font-semibold !text-md"
            enabled={isRecordingSessionEnabled}
            disabled={!canManageRecordingsSettings}
            setEnabled={() => {
              if (!canManageRecordingsSettings) return;
              try {
                mutateUpdateSiteRecording(
                  { siteId, enable: !isRecordingSessionEnabled },
                  {
                    onError: (error) =>
                      notifications.error({
                        content: `Turning ${recordingSessionStatus}  failed.`,
                        timeout: 3000,
                        context: { error },
                      }),
                    onSuccess: () => {
                      notifications.success({
                        content: `Recording is ${recordingSessionStatus}`,
                        timeout: 3000,
                      });
                    },
                  },
                );
              } catch (error) {
                console.error('mutateUpdateSiteRecording threw:', error);
                notifications.error({
                  content: `Turning ${recordingSessionStatus}  failed.`,
                  timeout: 3000,
                  context: { error },
                });
              }
            }}
            label={`Record visitors to ${siteName}`}
            displayLabel={true}
          />
          <p className="text-body-2 ml-[51px] mt-1 text-lynch-500">
            Crazy Egg will {!isRecordingSessionEnabled && 'NOT'} record visitors to your website.
          </p>
        </Panel>
      </div>
      <Formik
        key={siteId}
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, formikHelpers) => {
          if (!canManageRecordingsSettings) return;

          const {
            urlMatchingRules,
            endSessionOnBlockedUrl,
            blockedUrlMatchingRules,
            pageTargetingEnabledType,
            maskElements,
          } = values;

          // Strip empty entries before submission
          const cleanedUrlMatchingRules = urlMatchingRules.filter((r) => r.u !== '');
          const cleanedBlockedUrlMatchingRules = blockedUrlMatchingRules.filter((r) => r.u !== '');
          const cleanedMaskElements = maskElements.filter((el) => el.selector?.trim() !== '');

          try {
            await mutateRecordingSettingsAsync({
              siteId,
              pageTargetingEnabled:
                pageTargetingEnabledType === RECORD_SPECIFIC || pageTargetingEnabledType === RECORD_BY_JS,
              endSessionOnBlockedUrl,

              urlMatchingRules:
                cleanedUrlMatchingRules.length > 0 && pageTargetingEnabledType !== RECORD_BY_JS
                  ? `{"rules": ${JSON.stringify(returnArrayOfURLsForSubmission(cleanedUrlMatchingRules))}}`
                  : `{"rules" : []}`,

              blockedUrlMatchingRules:
                cleanedBlockedUrlMatchingRules.length > 0
                  ? `{"rules": ${JSON.stringify(returnArrayOfURLsForSubmission(cleanedBlockedUrlMatchingRules))}}`
                  : null,

              maskElements: cleanedMaskElements.length > 0 ? JSON.stringify(cleanedMaskElements) : null,
            });

            notifications.success({
              content: `Setting updated successfully for ${siteName}.`,
              timeout: 3000,
            });

            // Reset form with cleaned values so empty rows are removed
            formikHelpers.resetForm({
              values: {
                ...values,
                urlMatchingRules: cleanedUrlMatchingRules.length > 0 ? cleanedUrlMatchingRules : [],
                blockedUrlMatchingRules:
                  cleanedBlockedUrlMatchingRules.length > 0 ? cleanedBlockedUrlMatchingRules : [],
                maskElements: cleanedMaskElements.length > 0 ? cleanedMaskElements : [],
              },
            });
          } catch (error) {
            notifications.error({
              content: `Setting update failed for ${siteName}.`,
              timeout: 3000,
              context: { error },
            });
          }
        }}
      >
        {() => {
          return (
            <Form>
              <div className="mb-10">
                <OptionsHeader>What should we record?</OptionsHeader>
                <Panel>
                  <SiteSettingSectionRecordPage
                    siteName={resolvedSite?.name ?? siteName}
                    isRecordingSessionEnabled={canEditRecordingSettings}
                  />
                </Panel>
              </div>
              <div className="mb-10">
                <OptionsHeader>What should we ignore?</OptionsHeader>
                <Panel>
                  <SiteSettingsSectionIgnorePage isRecordingSessionEnabled={canEditRecordingSettings} />
                  <SiteSettingsSectionCode />
                  <SiteSettingsSectionMaskedElementsExplainer />
                  <SiteSettingsSectionMaskedElements isRecordingSessionEnabled={canEditRecordingSettings} />
                </Panel>
              </div>
              <AutoSave enabled={canManageRecordingsSettings} />
            </Form>
          );
        }}
      </Formik>
    </>
  );
}

function SiteSettingSectionRecordPage({ siteName, isRecordingSessionEnabled }) {
  const { values, setFieldValue } = useFormikContext();
  const { urlMatchingRules, pageTargetingEnabledType } = values;

  return (
    <>
      <RadioGroup
        size="sm"
        options={[
          {
            value: RECORD_ALL,
            label: `Record sessions when visitors land anywhere on ${siteName}`,
          },
          { value: RECORD_SPECIFIC, label: 'Only start recording sessions once visitors land on a specific page' },
          { value: RECORD_BY_JS, label: 'Start recording sessions when triggered via Javascript' },
        ]}
        value={pageTargetingEnabledType}
        onChange={(value) => {
          urlMatchingRules.length < 1 && value === RECORD_SPECIFIC
            ? setFieldValue('urlMatchingRules', [{ u: '' }])
            : null;
          setFieldValue('pageTargetingEnabledType', value);
        }}
        disabled={!isRecordingSessionEnabled}
      />
      {pageTargetingEnabledType === RECORD_SPECIFIC && (
        <div className="pt-4">
          <SiteSettingsModalListofInputs
            listData="urlMatchingRules"
            type="record"
            isRecordingSessionEnabled={isRecordingSessionEnabled}
          />
        </div>
      )}
      {pageTargetingEnabledType === RECORD_BY_JS && <SiteSettingsSectionCode section="record" />}
    </>
  );
}

function SiteSettingsSectionIgnorePage({ isRecordingSessionEnabled }) {
  const { values, setFieldValue } = useFormikContext();
  const { blockedUrlMatchingRules, endSessionOnBlockedUrl } = values;

  return (
    <div className="mb-[25px]">
      <p className="text-body-1">Which pages should we ignore?</p>
      <p className="text-body-4 mb-[15px] leading-tight">Choose which pages Crazy Egg shouldn’t record.</p>
      {blockedUrlMatchingRules.length > 0 && (
        <div className="mb-[15px]">
          <RadioGroup
            size="sm"
            options={[
              {
                value: false,
                label: `Skip these pages from recording`,
              },
              { value: true, label: 'End recording session when visitor reaches these pages' },
            ]}
            value={endSessionOnBlockedUrl}
            onChange={(value) => {
              setFieldValue('endSessionOnBlockedUrl', value);
            }}
            disabled={!isRecordingSessionEnabled}
          />
        </div>
      )}
      <SiteSettingsModalListofInputs
        listData="blockedUrlMatchingRules"
        type="ignore"
        showSingleDelete={true}
        isRecordingSessionEnabled={isRecordingSessionEnabled}
      />
    </div>
  );
}

function SiteSettingsSectionCode({ section }) {
  const isSectionRecord = section === 'record';

  return (
    <div className={classNames('w-full', { 'pb-[35px]': !isSectionRecord, 'pt-2': isSectionRecord })}>
      <p className="text-body-4 mb-2.5">
        You can also {isSectionRecord ? 'start' : 'stop'} a recording by calling{' '}
        <code className="rounded-[3px] border border-mystic-500 bg-white-lilac-500 px-1.25 text-center font-mono text-2xs">
          {isSectionRecord ? 'CE2.startRecording()' : 'CE2.stopRecording()'}
        </code>{' '}
        on your website like so:
      </p>
      <code className="mb-2.5 block whitespace-pre rounded bg-white-lilac-500 p-[15px] font-mono text-2xs text-black-pearl-500">
        {`(window.CE_API || (window.CE_API=[])).push(function(){\n${' '}${' '}${
          isSectionRecord ? 'CE2.startRecording()' : 'CE2.stopRecording()'
        };\n});`}
      </code>
      <SupportLink className="text-link text-xs font-normal" href={SupportLinks.siteSettings.sessionsTriggeredByJS}>
        Learn more about recording sessions triggered by JavaScript
      </SupportLink>
    </div>
  );
}

function SiteSettingsSectionMaskedElementsExplainer() {
  return (
    <div className="mb-5">
      <p className="text-body-1 mb-2.5">Which elements should we ignore?</p>
      <p className="text-body-2">For privacy reasons, Crazy Egg automatically masks the following elements:</p>
      <ul className="text-body-2 my-2.5 ml-5 list-disc">
        <li>Email, password, numeric, and text input fields</li>
        <li>All slider and dial controls</li>
      </ul>
      <p className="text-body-2">
        If you want to mask additional elements, you can add them below by clicking “Add element”, or enter the URL of a
        page with the element you&apos;d like to mask, then find and click on the element to add it to the mask list.{' '}
        <SupportLink className="text-link font-normal" href={SupportLinks.siteSettings.maskingElement}>
          Learn more about masking elements
        </SupportLink>
        .
      </p>
    </div>
  );
}

function SiteSettingsSectionMaskedElements({ isRecordingSessionEnabled }) {
  const { values, setFieldValue } = useFormikContext();
  const [selectedDevice, selectDevice] = useState(DESKTOP);
  const [isInteractive, setIsInteractive] = useState(false);
  const [pageUrl, setPageUrl] = useState('');
  const [count, setCount] = useState(0);
  const [normalizedUrl, setNormalizedUrl] = useState('');
  const [urlDirty, setUrlDirty] = useState(false);

  const validUrl = useMemo(() => normalizedUrl.trim() !== '', [normalizedUrl]);
  const validDevice = [DESKTOP, TABLET, PHONE].includes(selectedDevice);

  const maskElementsLength = useMemo(() => values.maskElements.length, [values]);
  const lastInputEmpty = useMemo(() => (values.maskElements.at(-1)?.selector ?? '') === '', [values]);

  const onElementSelected = useCallback(
    (event) => {
      let element = null;

      if (event.eventType === 'clickSelected') {
        element = event.payload;
      }

      if (element !== null && lastInputEmpty && !values.maskElements.some((el) => el.selector === element)) {
        const targetIndex = Math.max(maskElementsLength - 1, 0);
        setFieldValue(`maskElements.${targetIndex}.selector`, element);
      } else if (element !== null && !lastInputEmpty && !values.maskElements.some((el) => el.selector === element)) {
        const targetIndex = Math.max(maskElementsLength, 0);
        setFieldValue(`maskElements.${targetIndex}`, { selector: element });
      } else if (element === null && !lastInputEmpty) {
        const targetIndex = Math.max(maskElementsLength - 1, 0);
        setFieldValue(`maskElements.${targetIndex}.selector`, '');
      }
    },
    [lastInputEmpty, maskElementsLength, setFieldValue, values.maskElements],
  );

  return (
    <div className="w-full">
      <p className="text-body-1 mb-2.5">Mask these elements:</p>
      <div className="flex flex-col">
        <SiteSettingsModalListofInputs
          listData="maskElements"
          type="css"
          buttonName="Element"
          placeholder="e.g. input[type=hidden]"
          showSingleDelete={true}
          buttonDisabled={false}
          isRecordingSessionEnabled={isRecordingSessionEnabled}
          halfWidthInput={true}
        >
          <div className="flex flex-1 pl-2.5">
            <div className="flex-1">
              <Input
                placeholder="e.g. example.com"
                name={'blocking.url'}
                value={pageUrl}
                className="rounded-br-none rounded-tr-none"
                onChange={(e) => {
                  setPageUrl(e.target.value.trim());
                  if (urlDirty) {
                    setUrlDirty(false);
                  }
                }}
                disabled={!isRecordingSessionEnabled}
                error={isRecordingSessionEnabled && !validUrl && urlDirty ? 'URL is required.' : null}
              />
            </div>
            <Button
              variant="secondary"
              className="w-[180px] rounded-bl-none rounded-tl-none pl-2.5 text-[13px]"
              onClick={() => {
                setUrlDirty(true);
                setNormalizedUrl(normalizeUrl(pageUrl || '', { appendSlash: false }));
              }}
              disabled={!isRecordingSessionEnabled}
            >
              <SearchIcon className="mr-1.25 h-4 w-4 fill-current" />
              Find Element on Page
            </Button>
          </div>
        </SiteSettingsModalListofInputs>
      </div>
      {validUrl && validDevice && isRecordingSessionEnabled ? (
        <div className="mt-5 flex flex-col">
          <div className="flex flex-col rounded border border-mystic-500">
            <div className="border-b border-mystic-500 px-5 py-2.5">
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center space-x-1">
                  <span className="text-body-4 mr-[6px]">Device view:</span>
                  <div className="flex items-center space-x-0.5">
                    <Button
                      className={classNames(
                        'relative flex w-10 justify-center !rounded-br-none !rounded-tr-none !px-0',
                        {
                          '!cursor-default !border !border-dodger-blue-500 !bg-dodger-blue-500 text-white':
                            selectedDevice === DESKTOP,
                        },
                      )}
                      onClick={() => selectDevice(DESKTOP)}
                      variant="secondary"
                      disabled={selectedDevice === DESKTOP}
                    >
                      <DesktopIcon className="h-4 w-4 fill-current" aria-label="desktop" />
                    </Button>
                    <Button
                      className={classNames('relative flex w-10 justify-center !rounded-none !px-0', {
                        '!cursor-default !border !border-dodger-blue-500 !bg-dodger-blue-500 text-white':
                          selectedDevice === TABLET,
                      })}
                      onClick={() => selectDevice(TABLET)}
                      variant="secondary"
                      disabled={selectedDevice === TABLET}
                    >
                      <TabletIcon className="h-4 w-4 fill-current" aria-label="tablet" />
                    </Button>
                    <Button
                      className={classNames(
                        'relative flex w-10 justify-center !rounded-bl-none !rounded-tl-none !px-0',
                        {
                          '!cursor-default !border !border-dodger-blue-500 !bg-dodger-blue-500 text-white':
                            selectedDevice === PHONE,
                        },
                      )}
                      onClick={() => selectDevice(PHONE)}
                      variant="secondary"
                      disabled={selectedDevice === PHONE}
                    >
                      <MobileIcon className="h-4 w-4 fill-current" aria-label="mobile" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  <Tooltip tooltipContent="Interactive mode">
                    <Button
                      onClick={() => {
                        setIsInteractive((interactive) => {
                          const newValue = !interactive;
                          changeInteractiveMode(newValue, 'element-selector-frame');
                          return newValue;
                        });
                      }}
                      className={classNames('relative flex w-10 justify-center !px-0', {
                        '!border !border-dodger-blue-500 !bg-dodger-blue-500 text-white': isInteractive,
                      })}
                      variant="secondary"
                    >
                      <InteractiveIcon className="h-4 w-4 fill-current" />
                    </Button>
                  </Tooltip>
                  <Tooltip tooltipContent="Reload the page">
                    <Button
                      onClick={() => {
                        setCount((count) => count + 1);
                      }}
                      className="relative flex w-10 justify-center !px-0"
                      variant="secondary"
                    >
                      <ReloadIcon className="h-3 w-3 fill-current" />
                    </Button>
                  </Tooltip>
                </div>
              </div>
            </div>

            <ElementSelector
              key={`${normalizedUrl}:${selectedDevice}:${count}`}
              url={normalizedUrl}
              device={selectedDevice.toLowerCase()}
              editorPath="basic"
              event-name="page-interaction"
              onSelect={onElementSelected}
              className="aspect-[4/3] w-full"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
